import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlusIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useRole } from '@/hooks/use-role';
import { fillTemplate } from '@/lib/utils';
import {
  calendarFeedQueryKey,
  getCalendarFeedStatus,
  mintCalendarFeed,
  revokeCalendarFeed,
  type CalendarFeedDto,
} from '@/lib/api/calendar';
import { employeesQueryKey, listEmployees } from '@/lib/api/employees';

import { CalendarFeedLinks } from './calendar-feed-links';
import { formatFeedDate } from './helpers';

export function CalendarSyncSection() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { isOwner } = useRole();
  const { isPending: entitlementsPending, has } = useEntitlements();

  const queryClient = useQueryClient();
  const [minted, setMinted] = useState<CalendarFeedDto | null>(null);
  const [confirming, setConfirming] = useState<'regenerate' | 'revoke' | null>(
    null,
  );
  const [employeeId, setEmployeeId] = useState('');

  const locked = !entitlementsPending && !has('calendar_feed');

  const statusQuery = useQuery({
    queryKey: calendarFeedQueryKey(businessId ?? ''),
    queryFn: () => getCalendarFeedStatus(businessId!),
    enabled: Boolean(businessId) && !locked,
  });

  const employeesQuery = useQuery({
    queryKey: employeesQueryKey(businessId ?? '', 'active'),
    queryFn: () => listEmployees(businessId!, 'active'),
    enabled: Boolean(businessId) && isOwner && !locked,
  });

  const mintMutation = useMutation({
    mutationFn: () => mintCalendarFeed(businessId!),
    meta: { successMessage: t.calendarFeedReady },
    onSuccess: (data) => {
      setMinted(data);
      setConfirming(null);
      queryClient.setQueryData(calendarFeedQueryKey(businessId ?? ''), {
        active: true,
        created_at: new Date().toISOString(),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokeCalendarFeed(businessId!),
    meta: { successMessage: t.calendarFeedRevoked },
    onSuccess: () => {
      setMinted(null);
      setConfirming(null);
      queryClient.setQueryData(calendarFeedQueryKey(businessId ?? ''), {
        active: false,
        created_at: null,
      });
    },
  });

  const busy = mintMutation.isPending || revokeMutation.isPending;
  const status = statusQuery.data;
  const feedName = fillTemplate(t.calendarFeedName, {
    business: business?.business_name ?? 'VizitPro',
  });

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border bg-card p-5"
      data-testid="calendar-sync"
    >
      <div>
        <h2 className="text-sm font-semibold">{t.calendarSyncTitle}</h2>
        <p className="text-xs text-muted-foreground">
          {isOwner
            ? t.calendarSyncSubtitleOwner
            : t.calendarSyncSubtitleEmployee}
        </p>
      </div>

      {entitlementsPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : locked ? (
        <p className="text-sm text-muted-foreground border-t pt-4">
          {t.calendarSyncLocked}
        </p>
      ) : statusQuery.isPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : statusQuery.isError ? (
        <p className="text-sm text-muted-foreground">{t.calendarStatusError}</p>
      ) : minted ? (
        <div className="flex flex-col gap-4 border-t pt-4">
          {isOwner && employeesQuery.data && employeesQuery.data.length > 0 && (
            <label className="flex flex-col gap-1 text-xs font-medium">
              {t.calendarEmployeeLabel}
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                data-testid="calendar-employee-select"
                className="px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">{t.calendarEmployeeAll}</option>
                {employeesQuery.data.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <CalendarFeedLinks
            feed={minted}
            employeeId={employeeId || undefined}
            feedName={feedName}
          />

          <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
            <li>{t.calendarWarnPassword}</li>
            <li>{t.calendarWarnNotRealtime}</li>
            <li>{t.calendarWarnOneWay}</li>
          </ul>

          {confirming ? (
            <ConfirmRow
              message={
                confirming === 'regenerate'
                  ? t.calendarRegenerateConfirm
                  : t.calendarRevokeConfirm
              }
              cancelLabel={t.calendarCancel}
              confirmLabel={t.calendarConfirm}
              busy={busy}
              onCancel={() => setConfirming(null)}
              onConfirm={() =>
                confirming === 'regenerate'
                  ? mintMutation.mutate()
                  : revokeMutation.mutate()
              }
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setConfirming('regenerate')}
                data-testid="calendar-regenerate"
              >
                <RefreshCwIcon className="w-3.5 h-3.5" />
                {t.calendarRegenerate}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setConfirming('revoke')}
                data-testid="calendar-revoke"
              >
                <Trash2Icon className="w-3.5 h-3.5" />
                {t.calendarRevoke}
              </Button>
            </div>
          )}
        </div>
      ) : status?.active ? (
        <div className="flex flex-col gap-3 border-t pt-4">
          <p className="text-sm">
            {fillTemplate(t.calendarConnectedSince, {
              date: formatFeedDate(status.created_at),
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t.calendarShownOnceHint}
          </p>

          {confirming ? (
            <ConfirmRow
              message={
                confirming === 'regenerate'
                  ? t.calendarRegenerateConfirm
                  : t.calendarRevokeConfirm
              }
              cancelLabel={t.calendarCancel}
              confirmLabel={t.calendarConfirm}
              busy={busy}
              onCancel={() => setConfirming(null)}
              onConfirm={() =>
                confirming === 'regenerate'
                  ? mintMutation.mutate()
                  : revokeMutation.mutate()
              }
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={() => setConfirming('regenerate')}
                data-testid="calendar-regenerate"
              >
                <RefreshCwIcon className="w-3.5 h-3.5" />
                {t.calendarRegenerate}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setConfirming('revoke')}
                data-testid="calendar-revoke"
              >
                <Trash2Icon className="w-3.5 h-3.5" />
                {t.calendarRevoke}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3 border-t pt-4">
          <p className="text-sm text-muted-foreground">
            {t.calendarShownOnceHint}
          </p>
          <div>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => mintMutation.mutate()}
              data-testid="calendar-generate"
            >
              <CalendarPlusIcon className="w-3.5 h-3.5" />
              {t.calendarGenerate}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

interface ConfirmRowProps {
  message: string;
  cancelLabel: string;
  confirmLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

// Inline destructive-action confirm — mirrors the `confirmingDecline` pattern in
// `src/routes/quotes/quote-detail-modal.tsx` (no dialog library in this app).
function ConfirmRow({
  message,
  cancelLabel,
  confirmLabel,
  busy,
  onCancel,
  onConfirm,
}: ConfirmRowProps) {
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-900/50 dark:bg-amber-900/20"
      data-testid="calendar-confirm"
    >
      <p className="text-amber-800 dark:text-amber-300">{message}</p>
      <div className="flex gap-2">
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={busy}
          onClick={onCancel}
          data-testid="calendar-confirm-cancel"
        >
          {cancelLabel}
        </Button>
        <Button
          type="button"
          size="xs"
          disabled={busy}
          onClick={onConfirm}
          data-testid="calendar-confirm-ok"
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
